import { UploadResponse } from 'imagekit-javascript/dist/src/interfaces';
import { IKUpload } from 'imagekitio-react';
import { IKImageProps } from 'imagekitio-react/dist/types/components/IKImage/combinedProps';
import { IKUploadProps } from 'imagekitio-react/dist/types/components/IKUpload/props';
import React, { useRef, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Stack,
    Typography,
} from '@mui/material';

import { Env, ImageKitNamedTransforms } from '@barboxx/types';

import { Image } from './Image';

export interface ImageUploadProps
    extends Omit<IKUploadProps, 'ref' | 'style' | 'className' | 'onSuccess'> {
    label?: string;
    helperText?: string;
    error?: boolean;
    required?: boolean;
    imagekitProps?: Partial<IKImageProps>;
    onSuccess?: (res: UploadResponseWithVersion) => void;
    env?: Env;
}

export type UploadResponseWithVersion = UploadResponse & {
    versionInfo: { id: string; name: string };
};

const MAX_FILE_SIZE = 20000000;

export function ImageUpload({
    label = 'Upload Image',
    helperText,
    error = false,
    required = false,
    onSuccess,
    onError,
    onUploadProgress,
    onUploadStart,
    height,
    width,
    imagekitProps,
    folder,
    env = 'development',
    ...rest
}: ImageUploadProps) {
    const ikUploadRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    // const [fileName, setFileName] = useState<string | null>(null);
    const [uploadedImagePath, setUploadedImagePath] = useState<string | null>(
        null
    );
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number>(0);

    const handleClick = () => {
        if (ikUploadRef.current) {
            ikUploadRef.current.click();
        }
    };

    const handleError = (err: unknown) => {
        setUploading(false);
        setUploadProgress(0);
        onError?.(err as Error);
    };

    const handleUploadStart = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setUploading(true);
        setUploadProgress(0);
        onUploadStart?.(evt);

        if (evt.target?.files?.[0]) {
            const file = evt.target.files[0];
            // setFileName(file.name);

            const fileReader = new FileReader();
            fileReader.onload = e => {
                if (e.target?.result) {
                    setPreviewUrl(e.target.result as string);
                }
            };
            fileReader.readAsDataURL(file);
        }
    };

    const handleUploadProgress = (
        progress: ProgressEvent<XMLHttpRequestEventTarget>
    ) => {
        const percentage = Math.round((progress.loaded / progress.total) * 100);
        if (typeof percentage === 'number') {
            setUploadProgress(percentage);
        }
        onUploadProgress?.(progress);
    };

    const handleSuccess = (res: UploadResponse) => {
        setUploading(false);
        // setFileName(res.name || 'File uploaded successfully');
        setUploadedImagePath(res.filePath);
        setUploadProgress(100);
        onSuccess?.(res as UploadResponseWithVersion);
    };

    return (
        <FormControl fullWidth error={error} required={required}>
            {label && (
                <Typography
                    variant="subtitle1"
                    color="text.primary"
                    sx={{ mb: 1 }}
                >
                    {label} {required && '*'}
                </Typography>
            )}

            <Box
                onClick={handleClick}
                sx={theme => ({
                    borderColor: 'action.disabled',
                    borderStyle: 'dashed',
                    borderWidth: 1,
                    borderRadius: 2,
                    padding: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                    },
                })}
            >
                <Stack
                    direction="column"
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={1}
                    flex={1}
                >
                    {uploading ? (
                        <>
                            <CircularProgress
                                size={40}
                                variant="determinate"
                                value={uploadProgress}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {uploadProgress.toFixed(0)}% Uploaded
                            </Typography>
                        </>
                    ) : (
                        <ImagePreview
                            uploadedImagePath={uploadedImagePath}
                            previewUrl={previewUrl}
                            imagekitProps={imagekitProps}
                        />
                    )}

                    <Button
                        variant="contained"
                        component="span"
                        disabled={uploading}
                        startIcon={<CloudUploadIcon />}
                    >
                        {uploading
                            ? 'Uploading...'
                            : previewUrl || uploadedImagePath
                              ? 'Change Image'
                              : 'Browse Files'}
                    </Button>

                    <IKUpload
                        isPrivateFile={true}
                        responseFields={['tags', 'url']}
                        validateFile={file => file.size < MAX_FILE_SIZE}
                        useUniqueFileName={false}
                        overwriteFile={true}
                        overwriteAITags={true}
                        overwriteTags={true}
                        overwriteCustomMetadata={true}
                        onError={handleError}
                        onSuccess={handleSuccess}
                        onUploadProgress={handleUploadProgress}
                        onUploadStart={handleUploadStart}
                        style={{ display: 'none' }}
                        ref={ikUploadRef}
                        folder={`${env}/${folder}`}
                        {...rest}
                    />
                </Stack>
            </Box>

            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
}

interface ImagePreviewProps {
    uploadedImagePath: string | null;
    previewUrl: string | null;
    imagekitProps?: Partial<IKImageProps>;
}
function ImagePreview({
    uploadedImagePath,
    previewUrl,
    imagekitProps,
}: ImagePreviewProps) {
    const imageToShow = uploadedImagePath || previewUrl;

    if (!imageToShow) {
        return (
            <Stack alignItems="center" gap={2}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
                <Typography variant="body1" color="text.secondary">
                    Click to upload an image
                </Typography>
            </Stack>
        );
    }

    return (
        <Box width="100%" borderRadius={1} overflow="hidden">
            {uploadedImagePath ? (
                <Image
                    path={uploadedImagePath}
                    transformation={[
                        {
                            raw: ImageKitNamedTransforms.Web.cover,
                        },
                    ]}
                    loading="lazy"
                    lqip={{ raw: ImageKitNamedTransforms.Web.coverLq }}
                    width="100%"
                    height="auto"
                    {...imagekitProps}
                />
            ) : (
                <img
                    src={previewUrl ?? ''}
                    alt="Preview"
                    style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
            )}
        </Box>
    );
}
